/**
 * PhoneListEditor — PartnerPhone CRUD inline szerkesztő (M2)
 * Felhasználó több telefonszámot kezelhet label + number + primary flag-gel.
 */

import { addPartnerPhone, deletePartnerPhone, updatePartnerPhone } from '@/api/partners';
import { Button, Input } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useState } from 'react';

const MAX_PHONES = 5;

interface PhoneRow {
  id: string;
  label: string;
  number: string;
  isPrimary: boolean;
}

interface PhoneListEditorProps {
  partnerId: string;
  phones: PhoneRow[];
  onPhonesChange: () => void;
}

export function PhoneListEditor({ partnerId, phones, onPhonesChange }: PhoneListEditorProps) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [deleting, setDeleting] = useState<Record<string, boolean>>({});
  const [adding, setAdding] = useState(false);

  const setSavingKey = (key: string, val: boolean) => setSaving(prev => ({ ...prev, [key]: val }));
  const setDeletingKey = (key: string, val: boolean) =>
    setDeleting(prev => ({ ...prev, [key]: val }));

  const [newPhoneLabel, setNewPhoneLabel] = useState('');
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [addingNew, setAddingNew] = useState(false);

  const handleShowAddRow = useCallback(() => {
    if (phones.length >= MAX_PHONES) return;
    setNewPhoneLabel(t('partner.phoneLabelOther'));
    setNewPhoneNumber('');
    setAddingNew(true);
  }, [phones.length, t]);

  const handleAdd = useCallback(async () => {
    const label = newPhoneLabel.trim();
    const number = newPhoneNumber.trim();
    if (!number) return; // guard: don't send empty number to API
    setAdding(true);
    try {
      await addPartnerPhone(partnerId, {
        label: label || t('partner.phoneLabelOther'),
        number,
        isPrimary: phones.length === 0,
      });
      setAddingNew(false);
      setNewPhoneLabel('');
      setNewPhoneNumber('');
      onPhonesChange();
    } catch (err) {
      console.error('Failed to add phone:', err);
    } finally {
      setAdding(false);
    }
  }, [partnerId, phones.length, onPhonesChange, t, newPhoneLabel, newPhoneNumber]);

  const handleCancelAdd = useCallback(() => {
    setAddingNew(false);
    setNewPhoneLabel('');
    setNewPhoneNumber('');
  }, []);

  const handleBlurUpdate = useCallback(
    async (phone: PhoneRow, field: 'label' | 'number', value: string) => {
      const trimmed = value.trim();
      if (trimmed === phone[field]) return;
      setSavingKey(phone.id, true);
      try {
        await updatePartnerPhone(partnerId, phone.id, { [field]: trimmed });
        onPhonesChange();
      } catch {
        // silent
      } finally {
        setSavingKey(phone.id, false);
      }
    },
    [partnerId, onPhonesChange]
  );

  const handleSetPrimary = useCallback(
    async (phone: PhoneRow) => {
      if (phone.isPrimary) return;
      setSavingKey(phone.id, true);
      try {
        await updatePartnerPhone(partnerId, phone.id, { isPrimary: true });
        onPhonesChange();
      } catch {
        // silent
      } finally {
        setSavingKey(phone.id, false);
      }
    },
    [partnerId, onPhonesChange]
  );

  const handleDelete = useCallback(
    async (phone: PhoneRow) => {
      setDeletingKey(phone.id, true);
      try {
        await deletePartnerPhone(partnerId, phone.id);
        onPhonesChange();
      } catch {
        // silent
      } finally {
        setDeletingKey(phone.id, false);
      }
    },
    [partnerId, onPhonesChange]
  );

  return (
    <div className="space-y-3">
      {phones.map(phone => (
        <div
          key={phone.id}
          className="flex flex-wrap items-center gap-2 rounded-md border border-input bg-background p-2"
        >
          {/* Label */}
          <Input
            className="w-32 shrink-0 text-sm"
            defaultValue={phone.label}
            placeholder={t('partner.phoneLabelPlaceholder')}
            onBlur={e => handleBlurUpdate(phone, 'label', e.target.value)}
            disabled={saving[phone.id] ?? false}
            aria-label={t('partner.phoneLabel')}
          />

          {/* Number */}
          <Input
            className={`min-w-[10rem] flex-1 text-sm ${phone.isPrimary ? 'font-bold' : ''}`}
            defaultValue={phone.number}
            placeholder="+36 30 123 4567"
            onBlur={e => handleBlurUpdate(phone, 'number', e.target.value)}
            disabled={saving[phone.id] ?? false}
            aria-label={t('partner.phoneNumber')}
          />

          {/* Primary radio */}
          <label className="flex cursor-pointer items-center gap-1 text-sm text-muted-foreground">
            <input
              type="radio"
              name={`phone-primary-${partnerId}`}
              checked={phone.isPrimary}
              onChange={() => handleSetPrimary(phone)}
              disabled={saving[phone.id] ?? false}
              className="accent-primary"
            />
            {t('partner.phonePrimary')}
          </label>

          {/* Delete */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
            disabled={deleting[phone.id] ?? false}
            onClick={() => handleDelete(phone)}
            aria-label={`${t('common.delete')} ${phone.number}`}
          >
            ✕
          </Button>
        </div>
      ))}

      {addingNew && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-primary bg-background p-2">
          <Input
            className="w-32 shrink-0 text-sm"
            value={newPhoneLabel}
            onChange={e => setNewPhoneLabel(e.target.value)}
            placeholder={t('partner.phoneLabelPlaceholder')}
            aria-label={t('partner.phoneLabel')}
          />
          <Input
            className="min-w-[10rem] flex-1 text-sm"
            value={newPhoneNumber}
            onChange={e => setNewPhoneNumber(e.target.value)}
            placeholder="+36 30 123 4567"
            aria-label={t('partner.phoneNumber')}
            onKeyDown={e => {
              if (e.key === 'Enter') void handleAdd();
              if (e.key === 'Escape') handleCancelAdd();
            }}
          />
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={() => void handleAdd()}
            disabled={adding || !newPhoneNumber.trim()}
            className="text-sm"
          >
            {t('common.save')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCancelAdd}
            className="text-sm"
          >
            {t('common.cancel')}
          </Button>
        </div>
      )}

      {!addingNew && phones.length < MAX_PHONES && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleShowAddRow}
          className="text-sm"
        >
          + {t('partner.addPhone')}
        </Button>
      )}

      {phones.length >= MAX_PHONES && (
        <p className="text-sm text-muted-foreground">{t('partner.phoneMaxReached')}</p>
      )}
    </div>
  );
}
